import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createServiceProfile, updateServiceProfile, fetchDesignSystemTemplate } from '@/services/serviceProfiles';
import type { ServiceProfile } from '@/types';

interface ServiceFormProps {
  onClose: () => void;
  existingProfile?: ServiceProfile;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-card-surface)',
  borderRadius: '8px',
  padding: '24px',
  width: '100%',
  maxWidth: '600px',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px',
};

const inputStyle: React.CSSProperties = {
  padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: '6px',
  fontSize: '13px', width: '100%', boxSizing: 'border-box',
};

const helpTextStyle: React.CSSProperties = { fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' };

const fieldGroupStyle: React.CSSProperties = { marginBottom: '14px' };

const sectionStyle: React.CSSProperties = {
  marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid var(--color-border)',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px', marginTop: 0,
};

const buttonStyle: React.CSSProperties = {
  padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: 'none',
};

export function ServiceForm({ onClose, existingProfile }: ServiceFormProps) {
  const queryClient = useQueryClient();
  const isEditing = !!existingProfile;

  const [repo, setRepo] = useState(existingProfile?.repo || '');
  const [displayName, setDisplayName] = useState(existingProfile?.displayName || '');
  const [description, setDescription] = useState(existingProfile?.description || '');
  const [previewUrl, setPreviewUrl] = useState(existingProfile?.previewUrl || '');
  const [baseBranch, setBaseBranch] = useState(existingProfile?.baseBranch || 'develop');

  // Auth Strategies
  interface StrategyDraft {
    name: string;
    description: string;
    type: 'cookie' | 'bearer' | 'firebase_token' | 'none';
    loginPath: string;
    credentialKeys: string[];
    credentialValues: Record<string, string>;
    extraFieldKeys: string[];
    extraFieldValues: Record<string, string>;
    mappingKeys: string[];
    mappingValues: Record<string, string>;
    isDefault: boolean;
  }

  const buildInitialStrategies = (): StrategyDraft[] => {
    // If existing profile has auth strategies, use those
    if (existingProfile?.authStrategies && existingProfile.authStrategies.length > 0) {
      return existingProfile.authStrategies.map(s => ({
        name: s.name,
        description: s.description || '',
        type: s.type,
        loginPath: s.loginPath || '/api/auth/login',
        credentialKeys: s.credentials_masked ? Object.keys(s.credentials_masked) : ['email', 'password'],
        credentialValues: {},
        extraFieldKeys: s.extraFields ? Object.keys(s.extraFields) : [],
        extraFieldValues: s.extraFields || {},
        mappingKeys: s.bodyFieldMapping ? Object.keys(s.bodyFieldMapping) : [],
        mappingValues: s.bodyFieldMapping || {},
        isDefault: s.isDefault,
      }));
    }
    // If existing profile has legacy auth (non-none), migrate it as a single strategy
    if (existingProfile?.auth?.type && existingProfile.auth.type !== 'none') {
      return [{
        name: 'default',
        description: '',
        type: existingProfile.auth.type,
        loginPath: existingProfile.auth.loginPath || '/api/auth/login',
        credentialKeys: existingProfile.auth.credentials_masked ? Object.keys(existingProfile.auth.credentials_masked) : ['email', 'password'],
        credentialValues: {},
        extraFieldKeys: existingProfile.auth.extraFields ? Object.keys(existingProfile.auth.extraFields) : [],
        extraFieldValues: existingProfile.auth.extraFields || {},
        mappingKeys: existingProfile.auth.bodyFieldMapping ? Object.keys(existingProfile.auth.bodyFieldMapping) : [],
        mappingValues: existingProfile.auth.bodyFieldMapping || {},
        isDefault: true,
      }];
    }
    return [];
  };

  const [strategies, setStrategies] = useState<StrategyDraft[]>(buildInitialStrategies);
  const [expandedStrategy, setExpandedStrategy] = useState<number | null>(strategies.length > 0 ? 0 : null);

  // Tech Stack
  const [framework, setFramework] = useState(existingProfile?.techStack?.framework || '');
  const [uiLibrary, setUiLibrary] = useState(existingProfile?.techStack?.uiLibrary || '');
  const [designSystem, setDesignSystem] = useState(existingProfile?.techStack?.designSystem || '');
  const [designSystemFigmaKey, setDesignSystemFigmaKey] = useState(existingProfile?.techStack?.designSystemFigmaKey || '');

  const [designGuidelines, setDesignGuidelines] = useState(existingProfile?.designGuidelines || '');
  const [designSystemMd, setDesignSystemMd] = useState(existingProfile?.designSystemMd || '');
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [error, setError] = useState('');

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof createServiceProfile>[0]) => createServiceProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceProfiles'] });
      onClose();
    },
    onError: (err: Error) => setError(err.message || 'Failed to create'),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => updateServiceProfile(existingProfile!._id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceProfiles'] });
      queryClient.invalidateQueries({ queryKey: ['serviceProfile', existingProfile!._id] });
      onClose();
    },
    onError: (err: Error) => setError(err.message || 'Failed to update'),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!repo.trim() || !displayName.trim()) {
      setError('Repo and display name are required.');
      return;
    }

    // Validate strategy names are unique and non-empty
    for (const s of strategies) {
      if (!s.name.trim()) { setError('All auth strategies must have a name.'); return; }
    }
    const names = strategies.map(s => s.name.trim().toLowerCase());
    if (new Set(names).size !== names.length) { setError('Auth strategy names must be unique.'); return; }

    // Build auth strategies payload
    const authStrategies = strategies.map(s => {
      const credentials: Record<string, string> = {};
      for (const key of s.credentialKeys) { if (key && s.credentialValues[key]) credentials[key] = s.credentialValues[key]; }
      const extraFields: Record<string, string> = {};
      for (const key of s.extraFieldKeys) { if (key && s.extraFieldValues[key]) extraFields[key] = s.extraFieldValues[key]; }
      const bodyFieldMapping: Record<string, string> = {};
      for (const key of s.mappingKeys) { if (key && s.mappingValues[key]) bodyFieldMapping[key] = s.mappingValues[key]; }
      return {
        name: s.name.trim(),
        description: s.description.trim(),
        type: s.type,
        loginPath: s.loginPath.trim(),
        ...(Object.keys(credentials).length > 0 ? { credentials } : {}),
        extraFields,
        bodyFieldMapping,
        isDefault: s.isDefault,
      };
    });

    // Set legacy auth from the default strategy (backward compat)
    const defaultStrategy = strategies.find(s => s.isDefault) || strategies[0];
    const legacyAuth = defaultStrategy ? {
      type: defaultStrategy.type,
      loginPath: defaultStrategy.loginPath.trim(),
      extraFields: (() => {
        const ef: Record<string, string> = {};
        for (const key of defaultStrategy.extraFieldKeys) { if (key && defaultStrategy.extraFieldValues[key]) ef[key] = defaultStrategy.extraFieldValues[key]; }
        return ef;
      })(),
      bodyFieldMapping: (() => {
        const bfm: Record<string, string> = {};
        for (const key of defaultStrategy.mappingKeys) { if (key && defaultStrategy.mappingValues[key]) bfm[key] = defaultStrategy.mappingValues[key]; }
        return bfm;
      })(),
    } : { type: 'none' as const, loginPath: '', extraFields: {}, bodyFieldMapping: {} };

    const data = {
      repo: repo.trim(),
      displayName: displayName.trim(),
      description: description.trim(),
      previewUrl: previewUrl.trim(),
      baseBranch: baseBranch.trim(),
      auth: legacyAuth,
      authStrategies,
      techStack: {
        framework: framework.trim(),
        uiLibrary: uiLibrary.trim(),
        designSystem: designSystem.trim(),
        designSystemFigmaKey: designSystemFigmaKey.trim(),
      },
      designGuidelines: designGuidelines.trim(),
      designSystemMd: designSystemMd,
    };

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const addStrategy = useCallback(() => {
    const newStrategy: StrategyDraft = {
      name: '',
      description: '',
      type: 'cookie',
      loginPath: '/api/auth/login',
      credentialKeys: ['email', 'password'],
      credentialValues: {},
      extraFieldKeys: [],
      extraFieldValues: {},
      mappingKeys: [],
      mappingValues: {},
      isDefault: strategies.length === 0,
    };
    setStrategies(prev => [...prev, newStrategy]);
    setExpandedStrategy(strategies.length);
  }, [strategies.length]);

  const removeStrategy = useCallback((index: number) => {
    setStrategies(prev => prev.filter((_, i) => i !== index));
    setExpandedStrategy(null);
  }, []);

  const updateStrategy = useCallback((index: number, updates: Partial<StrategyDraft>) => {
    setStrategies(prev => prev.map((s, i) => {
      if (i !== index) {
        // If setting this one as default, clear default on others
        if (updates.isDefault) return { ...s, isDefault: false };
        return s;
      }
      return { ...s, ...updates };
    }));
  }, []);

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 20px 0' }}>
          {isEditing ? 'Edit Service Profile' : 'Register New Service'}
        </h2>
        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}>Basic Info</h3>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Repository *</label>
              <input style={inputStyle} type="text" value={repo} onChange={(e) => setRepo(e.target.value)}
                placeholder="e.g. preventivehealth-ai/app-shell" required disabled={isEditing} />
              <div style={helpTextStyle}>GitHub org/repo — used as the join key to HandoffPacks</div>
            </div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Display Name *</label>
              <input style={inputStyle} type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. App Shell" required />
            </div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Description</label>
              <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={description}
                onChange={(e) => setDescription(e.target.value)} placeholder="What does this service do?" />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ ...fieldGroupStyle, flex: 1 }}>
                <label style={labelStyle}>Preview URL</label>
                <input style={inputStyle} type="text" value={previewUrl} onChange={(e) => setPreviewUrl(e.target.value)}
                  placeholder="http://localhost:3003" />
              </div>
              <div style={{ ...fieldGroupStyle, flex: 1 }}>
                <label style={labelStyle}>Base Branch</label>
                <input style={inputStyle} type="text" value={baseBranch} onChange={(e) => setBaseBranch(e.target.value)}
                  placeholder="develop" />
              </div>
            </div>
          </div>

          {/* Auth Strategies */}
          <div style={sectionStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h3 style={sectionTitleStyle}>Auth Strategies</h3>
              <button type="button" onClick={addStrategy}
                style={{ ...buttonStyle, backgroundColor: '#ede9fe', color: '#5b21b6', fontSize: '12px', padding: '4px 12px' }}>
                + Add Login
              </button>
            </div>
            <div style={helpTextStyle}>
              Each strategy is a named login configuration (e.g. super-admin, workspace-admin, regular-user).
              The default strategy is used for screenshots when none is specified.
            </div>

            {strategies.length === 0 && (
              <div style={{ marginTop: '10px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '6px', fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center' }}>
                No auth strategies — service will be crawled without authentication.
                <br />Click "+ Add Login" to configure authentication.
              </div>
            )}

            {strategies.map((strat, si) => {
              const isOpen = expandedStrategy === si;
              return (
                <div key={si} style={{ marginTop: '8px', border: '1px solid var(--color-border)', borderRadius: '6px', overflow: 'hidden' }}>
                  {/* Strategy header — click to expand/collapse */}
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: strat.isDefault ? '#f0fdf4' : 'var(--bg-secondary)', cursor: 'pointer' }}
                    onClick={() => setExpandedStrategy(isOpen ? null : si)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{isOpen ? '▼' : '▶'}</span>
                      <span style={{ fontWeight: 600, fontSize: '13px' }}>{strat.name || '(unnamed)'}</span>
                      <span style={{ fontSize: '11px', padding: '1px 6px', borderRadius: '4px', background: '#dbeafe', color: '#1d4ed8' }}>{strat.type}</span>
                      {strat.isDefault && <span style={{ fontSize: '11px', padding: '1px 6px', borderRadius: '4px', background: '#dcfce7', color: '#166534' }}>default</span>}
                    </div>
                    <button type="button" onClick={(e) => { e.stopPropagation(); removeStrategy(si); }}
                      style={{ ...buttonStyle, backgroundColor: '#fee2e2', color: '#dc2626', padding: '2px 8px', fontSize: '11px' }}>Remove</button>
                  </div>

                  {/* Strategy detail — expanded */}
                  {isOpen && (
                    <div style={{ padding: '12px', borderTop: '1px solid var(--color-border)' }}>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <label style={labelStyle}>Name *</label>
                          <input style={inputStyle} value={strat.name} placeholder="e.g. super-admin"
                            onChange={e => updateStrategy(si, { name: e.target.value })} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={labelStyle}>Auth Type</label>
                          <select style={inputStyle} value={strat.type}
                            onChange={e => {
                              const newType = e.target.value as 'cookie' | 'bearer' | 'firebase_token' | 'none';
                              const updates: Partial<StrategyDraft> = { type: newType };
                              if (newType === 'firebase_token') {
                                if (!strat.extraFieldKeys.includes('firebaseApiKey')) {
                                  updates.extraFieldKeys = [...strat.extraFieldKeys, 'firebaseApiKey'];
                                }
                                if (!strat.mappingKeys.includes('idToken')) {
                                  updates.mappingKeys = [...strat.mappingKeys, 'idToken'];
                                }
                              }
                              updateStrategy(si, updates);
                            }}>
                            <option value="cookie">Cookie (session-based)</option>
                            <option value="bearer">Bearer Token</option>
                            <option value="firebase_token">Firebase Token</option>
                            <option value="none">None</option>
                          </select>
                        </div>
                      </div>
                      <div style={fieldGroupStyle}>
                        <label style={labelStyle}>Description</label>
                        <input style={inputStyle} value={strat.description} placeholder="e.g. Super admin with full access"
                          onChange={e => updateStrategy(si, { description: e.target.value })} />
                      </div>
                      {strat.type !== 'none' && (
                        <>
                          <div style={fieldGroupStyle}>
                            <label style={labelStyle}>Login Path</label>
                            <input style={inputStyle} value={strat.loginPath} placeholder="/api/auth/login"
                              onChange={e => updateStrategy(si, { loginPath: e.target.value })} />
                          </div>
                          {strat.type === 'firebase_token' && (
                            <div style={{ fontSize: '11px', color: '#92400e', backgroundColor: '#fef3c7', padding: '8px 10px', borderRadius: '6px', marginBottom: '8px' }}>
                              Firebase Token auth requires <strong>email</strong> + <strong>password</strong> in credentials (your Firebase user),
                              and <strong>firebaseApiKey</strong> in extra fields (Firebase Console &gt; Project Settings &gt; Web API Key).
                            </div>
                          )}
                          <div style={fieldGroupStyle}>
                            <label style={labelStyle}>Credentials {strat.type === 'firebase_token' ? '(required)' : ''}</label>
                            <div style={helpTextStyle}>
                              {isEditing ? 'Leave blank to keep existing. Enter new values to update.' : 'Key-value pairs sent in the login request body.'}
                            </div>
                            {strat.credentialKeys.map((key, ci) => (
                              <div key={ci} style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                                <input style={{ ...inputStyle, flex: 1 }} value={key} placeholder="key"
                                  onChange={e => {
                                    const newKeys = [...strat.credentialKeys]; newKeys[ci] = e.target.value;
                                    updateStrategy(si, { credentialKeys: newKeys });
                                  }} />
                                <input style={{ ...inputStyle, flex: 2 }} type="password" placeholder={isEditing ? '****' : 'value'}
                                  value={strat.credentialValues[key] || ''}
                                  onChange={e => updateStrategy(si, { credentialValues: { ...strat.credentialValues, [key]: e.target.value } })} />
                                <button type="button" onClick={() => updateStrategy(si, { credentialKeys: strat.credentialKeys.filter((_, j) => j !== ci) })}
                                  style={{ ...buttonStyle, backgroundColor: '#fee2e2', color: '#dc2626', padding: '2px 6px', fontSize: '11px' }}>x</button>
                              </div>
                            ))}
                            <button type="button"
                              onClick={() => updateStrategy(si, { credentialKeys: [...strat.credentialKeys, ''] })}
                              style={{ ...buttonStyle, backgroundColor: 'var(--bg-secondary)', color: 'var(--text-secondary)', marginTop: '4px', fontSize: '11px', padding: '2px 8px' }}>
                              + Add Credential
                            </button>
                          </div>
                          <div style={fieldGroupStyle}>
                            <label style={labelStyle}>Extra Fields (non-secret)</label>
                            <div style={helpTextStyle}>Plaintext fields sent alongside credentials (e.g. workspaceId, role)</div>
                            {strat.extraFieldKeys.map((key, ei) => (
                              <div key={ei} style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                                <input style={{ ...inputStyle, flex: 1 }} value={key} placeholder="key"
                                  onChange={e => {
                                    const newKeys = [...strat.extraFieldKeys]; newKeys[ei] = e.target.value;
                                    updateStrategy(si, { extraFieldKeys: newKeys });
                                  }} />
                                <input style={{ ...inputStyle, flex: 2 }} placeholder="value"
                                  value={strat.extraFieldValues[key] || ''}
                                  onChange={e => updateStrategy(si, { extraFieldValues: { ...strat.extraFieldValues, [key]: e.target.value } })} />
                                <button type="button" onClick={() => updateStrategy(si, { extraFieldKeys: strat.extraFieldKeys.filter((_, j) => j !== ei) })}
                                  style={{ ...buttonStyle, backgroundColor: '#fee2e2', color: '#dc2626', padding: '2px 6px', fontSize: '11px' }}>x</button>
                              </div>
                            ))}
                            <button type="button"
                              onClick={() => updateStrategy(si, { extraFieldKeys: [...strat.extraFieldKeys, ''] })}
                              style={{ ...buttonStyle, backgroundColor: 'var(--bg-secondary)', color: 'var(--text-secondary)', marginTop: '4px', fontSize: '11px', padding: '2px 8px' }}>
                              + Add Field
                            </button>
                          </div>
                          <div style={fieldGroupStyle}>
                            <label style={labelStyle}>Body Field Mapping</label>
                            <div style={helpTextStyle}>
                              Renames fields in the login request body. Source name &rarr; target name the API expects.
                              {strat.type === 'firebase_token' && ' For Firebase Token, "idToken" is the Firebase token field.'}
                            </div>
                            {strat.mappingKeys.map((key, mi) => (
                              <div key={mi} style={{ display: 'flex', gap: '6px', marginTop: '4px', alignItems: 'center' }}>
                                <input style={{ ...inputStyle, flex: 1 }} value={key} placeholder="source field"
                                  onChange={e => {
                                    const newKeys = [...strat.mappingKeys]; newKeys[mi] = e.target.value;
                                    updateStrategy(si, { mappingKeys: newKeys });
                                  }} />
                                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>&rarr;</span>
                                <input style={{ ...inputStyle, flex: 1 }} placeholder="target field"
                                  value={strat.mappingValues[key] || ''}
                                  onChange={e => updateStrategy(si, { mappingValues: { ...strat.mappingValues, [key]: e.target.value } })} />
                                <button type="button" onClick={() => updateStrategy(si, { mappingKeys: strat.mappingKeys.filter((_, j) => j !== mi) })}
                                  style={{ ...buttonStyle, backgroundColor: '#fee2e2', color: '#dc2626', padding: '2px 6px', fontSize: '11px' }}>x</button>
                              </div>
                            ))}
                            <button type="button"
                              onClick={() => updateStrategy(si, { mappingKeys: [...strat.mappingKeys, ''] })}
                              style={{ ...buttonStyle, backgroundColor: 'var(--bg-secondary)', color: 'var(--text-secondary)', marginTop: '4px', fontSize: '11px', padding: '2px 8px' }}>
                              + Add Mapping
                            </button>
                          </div>
                        </>
                      )}
                      <div style={{ marginTop: '4px' }}>
                        <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                          <input type="checkbox" checked={strat.isDefault}
                            onChange={e => updateStrategy(si, { isDefault: e.target.checked })} />
                          Default strategy (used for screenshots when none specified)
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Tech Stack */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}>Tech Stack</h3>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ ...fieldGroupStyle, flex: 1 }}>
                <label style={labelStyle}>Framework</label>
                <input style={inputStyle} type="text" value={framework} onChange={(e) => setFramework(e.target.value)} placeholder="React" />
              </div>
              <div style={{ ...fieldGroupStyle, flex: 1 }}>
                <label style={labelStyle}>UI Library</label>
                <input style={inputStyle} type="text" value={uiLibrary} onChange={(e) => setUiLibrary(e.target.value)} placeholder="MUI" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ ...fieldGroupStyle, flex: 1 }}>
                <label style={labelStyle}>Design System</label>
                <input style={inputStyle} type="text" value={designSystem} onChange={(e) => setDesignSystem(e.target.value)} placeholder="Name or Figma link" />
              </div>
              <div style={{ ...fieldGroupStyle, flex: 1 }}>
                <label style={labelStyle}>Figma Design System Key</label>
                <input style={inputStyle} type="text" value={designSystemFigmaKey} onChange={(e) => setDesignSystemFigmaKey(e.target.value)} placeholder="Figma file key" />
              </div>
            </div>
          </div>

          {/* Design System */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}>Design System</h3>
            <div style={helpTextStyle}>
              Full design system in Markdown — brand colors, typography, spacing. Primary context sent to AI for screen generation.
            </div>
            {isEditing ? (
              <div style={{ marginTop: '8px', padding: '12px', backgroundColor: 'var(--bg-secondary)', borderRadius: '6px', border: '1px solid var(--color-border)' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  {designSystemMd ? `${designSystemMd.split('\n').length} lines configured` : 'No design system configured'}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    window.location.href = `/ui-orchestrator/services/${existingProfile!._id}/design-system`;
                  }}
                  style={{ ...buttonStyle, backgroundColor: '#ede9fe', color: '#5b21b6', fontSize: '12px', padding: '6px 12px' }}
                >
                  Edit on Design System page
                </button>
              </div>
            ) : (
              <div style={{ position: 'relative', marginTop: '8px' }}>
                <textarea
                  style={{ ...inputStyle, minHeight: '300px', resize: 'vertical', fontFamily: 'monospace', fontSize: '12px', lineHeight: 1.5 }}
                  value={designSystemMd}
                  onChange={(e) => setDesignSystemMd(e.target.value)}
                  placeholder="Paste or write your design system markdown here..."
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    {designSystemMd ? `${designSystemMd.split('\n').length} lines` : '0 lines'}
                  </span>
                  <button
                    type="button"
                    disabled={loadingTemplate}
                    onClick={async () => {
                      setLoadingTemplate(true);
                      try {
                        const { content } = await fetchDesignSystemTemplate();
                        setDesignSystemMd(content);
                      } catch (err) {
                        setError(`Failed to load template: ${(err as Error).message}`);
                      } finally {
                        setLoadingTemplate(false);
                      }
                    }}
                    style={{ ...buttonStyle, backgroundColor: '#f0f9ff', color: '#0369a1', fontSize: '11px', padding: '4px 10px' }}
                  >
                    {loadingTemplate ? 'Loading...' : designSystemMd.trim() ? 'Reset to Default' : 'Import Default Template'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Design Guidelines */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Design Guidelines</label>
            <textarea style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} value={designGuidelines}
              onChange={(e) => setDesignGuidelines(e.target.value)} placeholder="Color rules, accessibility, spacing..." />
          </div>

          {error && <div style={{ color: '#dc2626', fontSize: '13px', marginBottom: '14px' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
            <button type="button" onClick={onClose}
              style={{ ...buttonStyle, backgroundColor: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>Cancel</button>
            <button type="submit" disabled={isPending}
              style={{ ...buttonStyle, backgroundColor: isPending ? '#93c5fd' : '#2563eb', color: '#fff' }}>
              {isPending ? 'Saving...' : isEditing ? 'Save Changes' : 'Register Service'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
